/* eslint-disable lit/no-invalid-html */
/* eslint-disable lit/binding-positions */
import { unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { html, literal } from 'lit/static-html.js';
import { BaseElement, customElement } from '../base-element';
import { Translate } from '../base-element/mixins/translation-mixin';
import '../buttons/icon-button/icon-button.wc';
import styles from './tag.scss?inline';

export enum DeployingStatuses {
  latest,
  outdated,
  live,
  primary,
  default,
  indicator,
  success,
  error,
  warning,
}

@customElement('ps-tag')
export class TagWC extends Translate(BaseElement) {
  static styles = unsafeCSS(styles);

  /**
   * Component size.
   */
  @property({ reflect: true }) size: 'small' | 'medium' | 'large' = 'small';

  /**
   * Component variant.
   */
  @property({ reflect: true, attribute: 'new-variant' }) newVariant:
    | 'outlined'
    | 'filled' = 'filled';

  /**
   * if added new variants, please update the enum in packages/translations/generated/i18nTypes-studio.ts
   * and add new translations in packages/translations/locales/studio in each language
   */
  @property({ reflect: true }) variant: keyof typeof DeployingStatuses =
    'default';

  /** When set, the underlying button will be rendered as an `<a>` with this `href` instead of a `<button>`. */
  @property() href?: HTMLAnchorElement['href'];

  /** If button rendered as an `<a>`, it is possible to set target attribute. */
  @property({ reflect: true }) target?: HTMLAnchorElement['target'];

  /** Indicates that tag is clickable */
  @property({ type: Boolean, reflect: true }) interactive = false;

  @property({ type: Boolean, reflect: true }) disabled = false;

  @property({ type: Boolean, reflect: true }) minimal = false;

  /** Makes the tag removable and shows a remove button. */
  @property({ type: Boolean }) removable = false;

  private handleRemoveClick(e: MouseEvent | KeyboardEvent) {
    e.stopPropagation();
    this.emit('ps-remove');
  }

  render() {
    const isLink = !!this.href;
    const isInteractive = (isLink || this.interactive) && !this.disabled;
    let tag = literal`span`;

    if (isLink) tag = literal`a`;
    else if (this.interactive) tag = literal`button`;

    return html`
    ${
      this.removable
        ? html`
            <ps-icon-button
              part="remove-button"
              exportparts="base:remove-button__base"
              name="close"
              rounded
              size="xsmall"
              label="Remove"
              class="c-tag__remove"
              @click=${this.handleRemoveClick}
              tabindex="-1"
              .disabled=${this.disabled}
            ></ps-icon-button>
          `
        : ''
    }
      <${tag}
        href=${ifDefined(isLink && !this.disabled ? this.href : undefined)}
        tabindex=${ifDefined(isInteractive) ? 0 : null}
        target=${ifDefined(isLink ? this.target : undefined)}
        class="${classMap({
          'c-tag': true,
          [`c-tag--size-${this.size}`]: this.size,
          [`c-tag--minimal`]: this.minimal,
          [`c-tag--variant-${this.variant}`]: this.variant,
          [`c-tag--type-${this.newVariant}`]: this.newVariant,
          'c-tag--interactive': isInteractive,
          'c-tag--removable': this.removable,
        })}"
      >
        <slot></slot>
      </${tag}>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-tag': TagWC;
  }
  enum PSElementTagNameMap {
    'ps-tag' = 'ps-tag',
  }
}
